import type { DriverLocation, PublicSnapshot } from "@paddock-signal/contracts";

type EstimatedLocation = Extract<DriverLocation, { status: "estimated" }>;

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface DriverCluster {
  coordinates: [longitude: number, latitude: number];
  driverIds: string[];
  locations: EstimatedLocation[];
}

export function clusterDriverLocations(
  locations: readonly EstimatedLocation[],
  project: (coordinates: [number, number]) => ScreenPoint,
  radiusPixels = 92,
): DriverCluster[] {
  const groups: EstimatedLocation[][] = [];
  for (const location of locations) {
    const point = project(location.coordinates);
    const matchingGroups = groups.filter((group) =>
      group.some((member) => {
        const memberPoint = project(member.coordinates);
        return (
          Math.hypot(point.x - memberPoint.x, point.y - memberPoint.y) <=
          radiusPixels
        );
      }),
    );
    if (matchingGroups.length === 0) {
      groups.push([location]);
      continue;
    }
    const destination = [location];
    for (const matchingGroup of matchingGroups) {
      destination.push(...matchingGroup);
      groups.splice(groups.indexOf(matchingGroup), 1);
    }
    groups.push(destination);
  }

  return groups.map((group) => ({
    coordinates: [
      group.reduce((total, item) => total + item.coordinates[0], 0) /
        group.length,
      group.reduce((total, item) => total + item.coordinates[1], 0) /
        group.length,
    ],
    driverIds: group.map((item) => item.driverId).sort(),
    locations: [...group].sort((left, right) =>
      left.driverId.localeCompare(right.driverId),
    ),
  }));
}

export function clusterBounds(
  cluster: DriverCluster,
): [[number, number], [number, number]] {
  const longitudes = cluster.locations.map((item) => item.coordinates[0]);
  const latitudes = cluster.locations.map((item) => item.coordinates[1]);
  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ];
}

function radians(value: number): number {
  return (value * Math.PI) / 180;
}

export function distanceKilometers(
  left: readonly [number, number],
  right: readonly [number, number],
): number {
  const latitude = radians(right[1] - left[1]);
  const longitude = radians(right[0] - left[0]);
  const leftLatitude = radians(left[1]);
  const rightLatitude = radians(right[1]);
  const haversine =
    Math.sin(latitude / 2) ** 2 +
    Math.cos(leftLatitude) *
      Math.cos(rightLatitude) *
      Math.sin(longitude / 2) ** 2;
  return 12_742.0176 * Math.asin(Math.sqrt(haversine));
}

const halfGlobeDistanceKilometers = 20_015.1;
export const minimumCircuitMarkerOpacity = 0.1;

/**
 * Fade circuit labels smoothly across the globe rather than switching them
 * between near and far states. The map is effectively flat once zoomed in,
 * so local labels remain fully opaque there; the global view never fades a
 * circuit below the requested 10% floor.
 */
export function circuitMarkerOpacity(
  distanceFromViewKilometers: number,
  zoom: number,
): number {
  if (zoom >= 5) return 1;
  const normalized = Math.min(
    1,
    Math.max(0, distanceFromViewKilometers / halfGlobeDistanceKilometers),
  );
  return Math.max(
    minimumCircuitMarkerOpacity,
    1 - (1 - minimumCircuitMarkerOpacity) * normalized,
  );
}

export function activeEstimatedLocations(
  snapshot: Pick<PublicSnapshot, "drivers" | "locations">,
  visibleDriverIds: ReadonlySet<string>,
): EstimatedLocation[] {
  const activeIds = new Set(
    snapshot.drivers
      .filter((driver) => driver.active && visibleDriverIds.has(driver.id))
      .map((driver) => driver.id),
  );
  return snapshot.locations.filter(
    (location): location is EstimatedLocation =>
      location.status === "estimated" && activeIds.has(location.driverId),
  );
}
